import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useTranslation } from "react-i18next";
import { Store } from "@/models/storeModel";
import { toast } from "sonner";
import { updateStore } from "@/services/stores/updateStore";
import { Shield, MapPin, Info } from "lucide-react";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { SimpleLocationPicker } from "../ui/simple-location-picker";

interface GeofencingSecurityCardProps {
  store: Store;
  onUpdate: () => void;
}

export function GeofencingSecurityCard({
  store,
  onUpdate,
}: GeofencingSecurityCardProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [geofencingEnabled, setGeofencingEnabled] = useState(
    store.geofencing_enabled || false
  );
  const [radius, setRadius] = useState<string>(
    store.order_radius_meters?.toString() || "1000"
  );
  const [location, setLocation] = useState<{
    latitude: number;
    longitude: number;
  }>({
    latitude: store.latitude || 0,
    longitude: store.longitude || 0,
  });
  const [tempLocation, setTempLocation] = useState(location);
  const { stores, selectedStoreId } = useStoreManagementStore();
  const currentStore = stores?.find((s) => s.id === selectedStoreId);

  // Return early if no store is provided
  if (!store) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Shield className="h-5 w-5" />
            <span>{t("geofencingSecurity.title")}</span>
          </CardTitle>
          <CardDescription>
            {t("geofencingSecurity.selectStore")}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const handleLocationSelect = (latitude: number, longitude: number) => {
    setTempLocation({ latitude, longitude });
  };

  const handleSaveLocation = async () => {
    if (!store?.id) {
      toast.error(t("geofencingSecurity.noStoreSelected"));
      return;
    }

    setIsLoading(true);
    try {
      const updatedStore = await updateStore(store.id, {
        latitude: tempLocation.latitude,
        longitude: tempLocation.longitude,
      });

      if (updatedStore) {
        setLocation(tempLocation);
        toast.success(t("storeLocationSecurity.locationSaved"));
        await onUpdate();
      } else {
        toast.error(t("storeLocationSecurity.saveFailed"));
      }
    } catch (error: any) {
      console.error("Failed to save location:", error);
      toast.error(error.message || t("storeLocationSecurity.saveFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const validateFields = () => {
    if (geofencingEnabled) {
      if (!location.latitude || !location.longitude) {
        toast.error(t("storeLocationSecurity.errors.locationRequired"));
        return false;
      }

      const radiusNum = parseInt(radius);
      if (isNaN(radiusNum) || radiusNum < 100 || radiusNum > 10000) {
        toast.error(t("storeLocationSecurity.errors.invalidRadius"));
        return false;
      }
    }
    return true;
  };

  const handleToggleGeofencing = async (enabled: boolean) => {
    if (!store?.id) {
      toast.error(t("geofencingSecurity.noStoreSelected"));
      return;
    }

    if (enabled && !validateFields()) {
      return;
    }

    setIsLoading(true);
    try {
      const updatedStore = await updateStore(store.id, {
        geofencing_enabled: enabled,
        latitude: enabled ? location.latitude : null,
        longitude: enabled ? location.longitude : null,
        order_radius_meters: enabled ? parseInt(radius) : null,
      });

      if (updatedStore) {
        setGeofencingEnabled(enabled);
        toast.success(
          enabled
            ? t("geofencingSecurity.enabled")
            : t("geofencingSecurity.disabled")
        );

        // Refresh store list
        await onUpdate();
      } else {
        toast.error(t("geofencingSecurity.updateFailed"));
      }
    } catch (error: any) {
      console.error("Failed to update geofencing setting:", error);
      toast.error(error.message || t("geofencingSecurity.updateFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center space-x-2">
          <Shield className="h-5 w-5" />
          <div className="flex items-baseline space-x-2">
            <CardTitle>
              {t("geofencingSecurity.title", "Geofencing Security")}
            </CardTitle>
            <span className="text-sm text-muted-foreground">
              ({t("currentStore", "Current Store")}: {currentStore?.name})
            </span>
          </div>
        </div>
        <CardDescription className="space-y-2">
          <p>
            {t(
              "geofencingSecurity.description",
              "Enable location-based security to restrict orders to customers within your store's vicinity."
            )}
          </p>
          <div className="mt-2 p-4 bg-muted rounded-lg text-sm space-y-2">
            <p className="font-medium flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              {t(
                "geofencingSecurity.howItWorks.title",
                "How Geofencing Works:"
              )}
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>
                {t(
                  "geofencingSecurity.howItWorks.point1",
                  "Customers must be within your defined radius to place orders"
                )}
              </li>
              <li>
                {t(
                  "geofencingSecurity.howItWorks.point2",
                  "Prevents fraudulent orders from distant locations"
                )}
              </li>
              <li>
                {t(
                  "geofencingSecurity.howItWorks.point3",
                  "Enhances security by verifying customer proximity"
                )}
              </li>
              <li>
                {t(
                  "geofencingSecurity.howItWorks.point4",
                  "Works in conjunction with other security measures"
                )}
              </li>
            </ul>
          </div>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center space-x-2">
          <Switch
            id="geofencing-enabled"
            checked={geofencingEnabled}
            onCheckedChange={handleToggleGeofencing}
            disabled={isLoading}
          />
          <Label
            htmlFor="geofencing-enabled"
            className="flex items-center space-x-2"
          >
            <span>
              {geofencingEnabled
                ? t("geofencingSecurity.enabled", "Geofencing Enabled")
                : t("geofencingSecurity.disabled", "Geofencing Disabled")}
            </span>
          </Label>
        </div>

        {geofencingEnabled && (
          <div className="space-y-4 mt-4 border-t pt-4">
            <div className="grid gap-2">
              <div className="flex items-center gap-2">
                <Label>{t("storeLocationSecurity.location")}</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="h-4 w-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t("storeLocationSecurity.tooltips.location")}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <SimpleLocationPicker
                onLocationSelect={handleLocationSelect}
                initialLocation={location}
                onSaveLocation={handleSaveLocation}
              />
            </div>

            <div className="grid gap-2">
              <div className="flex items-center gap-2">
                <Label>{t("storeLocationSecurity.orderRadius")}</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="h-4 w-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t("storeLocationSecurity.tooltips.radius")}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Input
                type="number"
                value={radius}
                onChange={(e) => setRadius(e.target.value)}
                min="100"
                max="10000"
              />
              <p className="text-sm text-muted-foreground">
                {t("storeLocationSecurity.orderRadiusDescription")}
              </p>
            </div>
          </div>
        )}

        <div className="text-sm text-muted-foreground">
          {geofencingEnabled ? (
            <p className="text-green-600">
              {t(
                "geofencingSecurity.enabledDescription",
                "Orders are restricted to customers within your store's defined radius for enhanced security."
              )}
            </p>
          ) : (
            <p className="text-amber-600">
              {t(
                "geofencingSecurity.disabledDescription",
                "All customers can place orders regardless of their location. Consider enabling for better security."
              )}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
